import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../customHooks/useAuth";

/**
 * Login
 * -----
 * Pagina di autenticazione (route pubblica).
 *
 * Responsabilità:
 * - Gestire lo stato locale del form (email/password)
 * - Invocare la logica di login esposta dal contesto Auth
 * - Dopo autenticazione, reindirizzare l’utente alla rotta originariamente richiesta
 *   (se presente) oppure a una rotta di default ("/overview")
 */
export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const auth = useAuth();
  const loginBtnDisabled = useMemo(() => {
    return email.length <= 0 || password.length <= 0;
  }, [email.length, password.length]);

  const updateEmail = useCallback((event: any) => {
    setEmail(event.target.value);
  }, []);

  const updatePassword = useCallback((event: any) => {
    setPassword(event.target.value);
  }, []);

  const loginHandler = useCallback(() => {
    auth.logIn!(email, password);
  }, [auth, email, password]);

  const location = useLocation();
  const from = (location.state as any)?.from || "/overview";

  useEffect(() => {
    if (auth.isLogged) {
      navigate(from, { replace: true });
    }
  }, [auth.isLogged, navigate, from]);

  const enterHandler = useCallback(
    (event: any) => {
      // Check if the key pressed is 'Enter'
      if (event.key === "Enter" || event.keyCode === 13) {
        loginHandler();
      }
    },
    [loginHandler]
  );

  return !auth.isLogged ? (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        backgroundImage: "url('img/trendrating-crm-cover.jpg')",
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          boxShadow={5}
          sx={{
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(255,255,255,0.5)",
          }}
          p={4}
          borderRadius={2}
          display={"flex"}
          flexDirection={"column"}
          gap={5}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <img
              draggable={false}
              width={300}
              height={"auto"}
              src="img/trendrating_crm_logo.png"
              alt="trendrating logo"
            />
          </Box>

          <Box
            width={"100%"}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            gap={2}
          >
            <TextField
              onKeyUp={enterHandler}
              fullWidth
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
              }}
              value={email}
              placeholder="Email"
              onChange={updateEmail}
              type="text"
              color="secondary"
              size="small"
            />

            <TextField
              color="secondary"
              onKeyUp={enterHandler}
              fullWidth
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
              }}
              onChange={updatePassword}
              value={password}
              // label={"Password"}
              placeholder="Password"
              type="password"
              size="small"
            />
            <Button
              disabled={loginBtnDisabled}
              color="secondary"
              sx={{ width: "auto", cursor: "pointer" }}
              onClick={() => loginHandler()}
            >
              {auth.isLoading ? (
                <Typography alignItems={"center"} display={"flex"} gap={1}>
                  <CircularProgress size={"1rem"} /> Loging...
                </Typography>
              ) : (
                <Typography>Login</Typography>
              )}
            </Button>
            <Typography fontSize={"0.6vw"} variant="caption">
              {`v ${process.env.REACT_APP_VERSION}`}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  ) : null;
}
